import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ORG_TYPES = [
  "school",
  "church",
  "community",
  "event",
  "corporate",
  "government",
  "other",
] as const;

const AGE_GROUPS = [
  "primary",
  "high_school",
  "youth",
  "adult",
  "senior",
  "mixed",
] as const;

const PROVINCES = [
  "Gauteng",
  "Western Cape",
  "KwaZulu-Natal",
  "Eastern Cape",
  "Free State",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
  "Other",
] as const;

export const SA_PROVINCES = PROVINCES;
export const ORGANISATION_TYPES = ORG_TYPES;
export const AUDIENCE_AGE_GROUPS = AGE_GROUPS;

const bookingInput = z.object({
  organisation: z.string().trim().min(2).max(200),
  organisation_type: z.enum(ORG_TYPES).default("school"),
  contact_name: z.string().trim().min(2).max(120),
  contact_email: z.string().trim().email().max(255),
  contact_phone: z.string().trim().max(40).optional().default(""),
  province: z.enum(PROVINCES).default("Gauteng"),
  city: z.string().trim().max(120).optional().default(""),
  venue: z.string().trim().max(200).optional().default(""),
  audience_size: z
    .number()
    .int()
    .min(0)
    .max(50000)
    .optional()
    .default(0),
  audience_age_group: z.enum(AGE_GROUPS).default("mixed"),
  preferred_date: z.string().optional(),
  preferred_time: z.string().trim().max(40).optional().default(""),
  topics: z.string().trim().max(1000).optional().default(""),
  message: z.string().trim().max(4000).optional().default(""),
});

export const requestAwarenessBooking = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => bookingInput.parse(i))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error, data: row } = await supabaseAdmin
      .from("awareness_bookings")
      .insert({
        ...data,
        preferred_date: data.preferred_date || null,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data: isAdmin } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden");
}

export const adminListAwareness = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("awareness_bookings")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return { bookings: data ?? [] };
  });

export const adminUpdateAwareness = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z
          .enum(["requested", "confirmed", "completed", "declined", "cancelled"])
          .optional(),
        admin_notes: z.string().max(4000).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { id, ...patch } = data;
    const { error } = await supabaseAdmin
      .from("awareness_bookings")
      .update(patch)
      .eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
